import { useEffect, useState } from "react";
import config from "../../config";
import { getActiveLoginAnnouncement } from "../../services/SystemAnnouncement";
import "./LoginAnnouncement.css";

const DEFAULT_VARIANT = "info";
const ALLOWED_VARIANTS = new Set(["info", "warning", "critical", "success"]);

const toCleanString = (value) =>
  typeof value === "string" ? value.trim() : "";

const normalizeHref = (value) => {
  const url = toCleanString(value);
  if (
    !url ||
    url.startsWith("/") ||
    url.startsWith("#") ||
    /^[a-z][a-z0-9+.-]*:/i.test(url)
  ) {
    return url;
  }

  return `https://${url}`;
};

function isConfigured(announcementConfig) {
  const title = toCleanString(announcementConfig?.title);
  const message = toCleanString(announcementConfig?.message);
  const linkLabel = toCleanString(announcementConfig?.linkLabel);
  const linkUrl = toCleanString(announcementConfig?.linkUrl);

  if (!title && !message && !(linkLabel && linkUrl)) {
    return false;
  }

  return true;
}

function AnnouncementBanner({ announcement }) {
  const title = toCleanString(announcement?.title);
  const message = toCleanString(announcement?.message);
  const linkLabel = toCleanString(announcement?.linkLabel);
  const linkUrl = normalizeHref(announcement?.linkUrl);
  const openInNewTab = announcement?.openInNewTab !== false;

  if (!isConfigured(announcement)) {
    return null;
  }

  const variant = ALLOWED_VARIANTS.has(announcement?.variant)
    ? announcement.variant
    : DEFAULT_VARIANT;

  return (
    <section
      className={`login-announcement login-announcement--${variant}`}
      role="status"
      aria-live="polite"
    >
      {title && <h2 className="login-announcement__title">{title}</h2>}
      {message && <p className="login-announcement__message">{message}</p>}

      {linkLabel && linkUrl && (
        <a
          className="login-announcement__link"
          href={linkUrl}
          target={openInNewTab ? "_blank" : undefined}
          rel={openInNewTab ? "noopener noreferrer" : undefined}
        >
          {linkLabel}
        </a>
      )}
    </section>
  );
}

function LoginAnnouncement() {
  const [announcement, setAnnouncement] = useState(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let active = true;

    const load = async () => {
      try {
        const apiAnnouncement = await getActiveLoginAnnouncement();
        if (active) {
          setAnnouncement(apiAnnouncement);
        }
      } catch (_) {
        const fallback = config.announcements?.login || {};
        if (active && fallback.enabled === true) {
          setAnnouncement(fallback);
        }
      } finally {
        if (active) {
          setLoaded(true);
        }
      }
    };

    load();

    return () => {
      active = false;
    };
  }, []);

  if (!loaded || !announcement) {
    return null;
  }

  return <AnnouncementBanner announcement={announcement} />;
}

export { AnnouncementBanner, ALLOWED_VARIANTS };
export default LoginAnnouncement;
